/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
} from 'antd';
import { CheckCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getSeguridadSocialCambio,
  getSeguridadSocialCambios,
  patchSeguridadSocialCambio,
  postSeguridadSocialCambio,
  deleteSeguridadSocialCambio,
  autorizarSeguridadSocialCambio,
} from 'api/nominas/seguridadSocialCambios';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import { getCatalogosDeModalidadDeSeguridadSocial } from 'api/catalogos/ModalidadDeSeguridadSocial';
import { getTiposDeNominasEmpleados } from 'api/nominas/catalogos/tiposNominasEmpleados';
import {
  getPlaza, getPlazas, getPlazasvistaOptimized, getPlazavistaOptimized,
} from 'api/catalogos/plazas';
import { getEmpleadosConDatosOpFilteredPaginated, getEmpleadosOptimized } from 'api/estructuraOrganizacional/empleados';
import { getJornadaDetalle } from 'api/catalogos/JornadaDetalle';
import CustomSearch from 'components/customSearch';

const SeguridadSocialCambios = ({ permission }) => {
  const [form] = Form.useForm();
  const [isAdding, setAdding] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [modalidadSeguridadSocial, setModalidadSeguridadSocial] = useState([]);
  const [nominaEmpleado, setNominaEmpleado] = useState([]);
  const [plazas, setPlazas] = useState([]);
  const [empleadosOptimized, setEmpleadosOptimized] = useState([]);

  const [puestoSelected, setPuestoSelected] = useState(null);

  const [_selectedEmpleadoSearch, setSelectedEmpleadoSearch] = useState();
  const [openSearch, setVisibleSearch] = useState(false);
  const [tiposDeNominaEmpleado, setTiposDeNominaEmpleado] = useState([]);
  const [baseURIPagination, setBaseURIPagination] = useState();
  const [dataSearch, setDataSearch] = useState([]);
  const [puestosPorEmpleado, setPuestosPorEmpleado] = useState({});

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getSeguridadSocialCambios();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _tipoDeNominaEmpleado,
        _ModalidadSeguridadSocial,
        _plazas,
        _empleados,
        _estados,
        _data,
      ] = await Promise.all([
        getTiposDeNominasEmpleados(),
        getCatalogosDeModalidadDeSeguridadSocial(),
        getPlazasvistaOptimized(),
        getEmpleadosOptimized(),
        getEstadosGlobales(),
        getSeguridadSocialCambios(),
      ]);
      if (mounted) {
        const modalidadSeguridadSocialAdapted = _ModalidadSeguridadSocial
          .filter((e) => e.modalidad && e.modalidad.trim() !== '') // keep only non-empty modalidad
          .map((modSegSocial) => {
            const _seguridadSocial = _ModalidadSeguridadSocial.find(
              (s) => s.id === modSegSocial.seguridad_social_padre,
            );
            return {
              id: modSegSocial.id,
              descripcion: `${_seguridadSocial.seguridad_social} - ${modSegSocial.modalidad}`,
            };
          });
        const tipoDeNominaEmpleadoAdapted = _tipoDeNominaEmpleado
          .map((modSegSocial) => ({
            id: modSegSocial.id,
            descripcion: modSegSocial.descripcion,
          }));
        setNominaEmpleado(tipoDeNominaEmpleadoAdapted);
        setTiposDeNominaEmpleado(_tipoDeNominaEmpleado);
        setModalidadSeguridadSocial(modalidadSeguridadSocialAdapted);
        setPlazas(_plazas);
        setEmpleadosOptimized(_empleados);
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  let baseURIDatosOpPaginado = 'catalogos/dato-operacional-del-empleado-paginado/?empleados_filter=';

  const findPuesto = async (empleados) => {
    const nuevosPuestos = {};
    await Promise.all(
      empleados.map(async (emp) => {
        try {
          const plazaEmp = await getPlaza(emp.plaza);
          nuevosPuestos[emp.id] = plazaEmp?.puesto_descripcion || '';
        } catch {
          nuevosPuestos[emp.id] = '';
        }
      }),
    );

    setPuestosPorEmpleado(nuevosPuestos);
  };

  const searchData = async (empId, nombreEmp, apPatEmp, apMatEmp, tipoNominaEmpleado) => {
    setLoading(true);
    if (empId) {
      baseURIDatosOpPaginado = `${baseURIDatosOpPaginado}numero_de_empleado=${empId};`;
    }
    if (nombreEmp) {
      baseURIDatosOpPaginado = `${baseURIDatosOpPaginado}nombre=${nombreEmp};`;
    }
    if (apPatEmp) {
      baseURIDatosOpPaginado = `${baseURIDatosOpPaginado}apellido_paterno=${apPatEmp};`;
    }
    if (apMatEmp) {
      baseURIDatosOpPaginado = `${baseURIDatosOpPaginado}apellido_materno=${apMatEmp};`;
    }
    if (tipoNominaEmpleado) {
      baseURIDatosOpPaginado = `${baseURIDatosOpPaginado}&tipo_de_nomina_empleado=${tipoNominaEmpleado}&`;
    }
    setBaseURIPagination(baseURIDatosOpPaginado);
    const _empleados = await getEmpleadosConDatosOpFilteredPaginated(baseURIDatosOpPaginado);
    await findPuesto(_empleados.results);
    setDataSearch(_empleados);
    return setLoading(false);
  };

  const validateInput = async () => {
    setLoading(true);
    setSelectedEmpleadoSearch();
    const searchEmp = form.getFieldValue('empleado_searcher');
    if (!searchEmp) {
      setVisibleSearch(true);
      return setLoading(false);
    }
    const res = await getEmpleadosConDatosOpFilteredPaginated(
      `${baseURIDatosOpPaginado}numero_de_empleado=${searchEmp};`,
    );

    const result = res?.results?.[0];

    if (!res) {
      return setLoading(false);
    }
    const empleadoResult = result?.empleado;
    if (empleadoResult) {
      form.setFieldsValue({
        empleado: empleadoResult?.id,
        plaza: result.plaza,
      });
    } else {
      form.resetFields(['empleado', 'plaza']);
    }
    // onChangeEmpleado(empleadoResult?.id);
    return setLoading(false);
  };

  const columnsEmpleado = [
    {
      titleText: 'No. de Empleado',
      dataIndex: 'empleado',
      key: 'empleado',
      width: 200,
      render: (empObject) => empObject?.numero_de_empleado,
    },
    {
      titleText: 'Nombre',
      dataIndex: 'empleado',
      key: 'empleado',
      width: 250,
      render: (empObject) => empObject?.nombre,
    },
    {
      titleText: 'Apellido Paterno',
      dataIndex: 'empleado',
      key: 'empleado',
      width: 250,
      render: (empObject) => empObject?.apellido_paterno,
    },
    {
      titleText: 'Apellido Materno',
      dataIndex: 'empleado',
      key: 'empleado',
      width: 250,
      render: (empObject) => empObject?.apellido_materno,
    },
    {
      titleText: 'Puesto',
      dataIndex: 'empleado',
      key: 'empleado',
      width: 300,
      render: (_, empObject) => puestosPorEmpleado[empObject.id],
    },
    {
      titleText: 'Tipo de Nómina Empleado',
      dataIndex: 'tipo_de_nomina_empleado',
      key: 'tipo_de_nomina_empleado',
      width: 250,
      render: (tneEmp) => tiposDeNominaEmpleado?.find((tne) => tne?.id === tneEmp)?.descripcion,
    },
  ];

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    setAdding(false);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _nivelDeEstudio = await getSeguridadSocialCambio(match.id);
    setTimeout(() => {
      if (_nivelDeEstudio) {
        form.resetFields();
        setTimeout(() => {
          _nivelDeEstudio.empleado_searcher = _nivelDeEstudio?.empleado;
          form.setFieldsValue(formatReceived(_nivelDeEstudio));
          setDisableFields(_nivelDeEstudio?.estados_globales !== ESTATUS_ACTIVO);
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };
  const onClickAdd = () => {
    setAdding(true);
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const valuesForm = form.getFieldsValue();
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len

      const values = {
        ...formatReceived(valuesForm),
        fecha_inicial: valuesForm.fecha_inicial
          ? valuesForm.fecha_inicial.format('YYYY-MM-DD')
          : null,
        fecha_final: valuesForm.fecha_final
          ? valuesForm.fecha_final.format('YYYY-MM-DD')
          : null,
        puesto: puestoSelected,
        seguridad_social_anterior: key
          ? valuesForm.seguridad_social_anterior
          : valuesForm.seguridad_social_nueva,
      };
      const saved = await (key
        ? patchSeguridadSocialCambio(key, values)
        : postSeguridadSocialCambio(values)
      );
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteSeguridadSocialCambio(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar la Asignación de Seguridad Social ya autorizada');
        return;
      }
      const saved = await autorizarSeguridadSocialCambio(key);
      if (saved) {
        const _data = await getSeguridadSocialCambios();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Tipo de Nómina Empleado',
      dataIndex: 'tipo_de_nomina_empleado',
      key: 'tipo_de_nomina_empleado',
      width: 220,
      render: (val) => nominaEmpleado.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'No. y Nombre de Empleado',
      dataIndex: 'empleado',
      key: 'empleado',
      width: 250,
      render: (val) => empleadosOptimized.find((e) => e.id === val)?.numero_y_nombre_completo,
    },
    {
      titleText: 'Área',
      dataIndex: 'plaza',
      key: 'plaza',
      width: 200,
      render: (val) => plazas.find((e) => e.id === val)?.directorio_area_clave_y_nombre,
    },
    {
      titleText: 'Puesto',
      dataIndex: 'plaza',
      key: 'plaza',
      width: 200,
      render: (val) => {
        const puesto = plazas.find((e) => e.id === val);
        const puesto_nombre_e_Id = puesto ? `${puesto.puesto_id} - ${puesto.puesto}` : '';
        return puesto_nombre_e_Id;
      },
    },
    {
      titleText: 'No. de Plaza',
      dataIndex: 'plaza',
      key: 'plaza',
      width: 200,
      render: (val) => plazas.find((e) => e.id === val)?.clave_y_descripcion,
    },
    {
      titleText: 'Seguridad Social Anterior',
      dataIndex: 'seguridad_social_anterior',
      key: 'seguridad_social_anterior',
      width: 220,
      render: (val) => modalidadSeguridadSocial.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'Seguridad Social Nueva',
      dataIndex: 'seguridad_social_nueva',
      key: 'seguridad_social_nueva',
      width: 220,
      render: (val) => modalidadSeguridadSocial.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'Fecha Inicial',
      dataIndex: 'fecha_inicial',
      key: 'fecha_inicial',
      width: 200,
      render: (val) => val?.format('YYYY-MM-DD'),
    },
    {
      titleText: 'Fecha Final',
      dataIndex: 'fecha_final',
      key: 'fecha_final',
      width: 200,
      render: (val) => val?.format('YYYY-MM-DD'),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estatus',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'seguridadsocialcambio', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
              className: 'success',
            },
          ]}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
          allowExportParam={false}
          allowSearch={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Asignación de Seguridad Social`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
            }}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="tipo_de_nomina_empleado"
                rules={rules.required}
                label="Tipo de Nómina Empleado"
                hasFeedback
              >
                <Select
                  dataSource={nominaEmpleado}
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Row align="middle">
                <Form.Item
                  label="Buscador de Empleado"
                  name="empleado_searcher"
                  hasFeedback
                >
                  <Input
                    allowClear
                  />
                </Form.Item>
                <Button
                  loading={loading}
                  onClick={validateInput}
                  icon={<SearchOutlined />}
                  style={{
                    width: '2.7rem',
                    height: '2rem',
                  }}
                />
              </Row>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="empleado"
                label="No. y Nombre del Empleado"
                rules={rules.required}
              >
                <Select
                  labelProp="numero_y_nombre_completo"
                  keyProp="id"
                  dataSource={empleadosOptimized}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="plaza"
                label="Área"
              >
                <Select
                  labelProp="directorio_area_clave_y_nombre"
                  keyProp="id"
                  dataSource={plazas}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="plaza"
                label="Puesto"
              >
                <Select
                  keyProp="id"
                  dataSource={plazas}
                  render={(val) => {
                    if (val) {
                      setPuestoSelected(val.puesto_id);
                      return `${val.puesto_id} - ${val.puesto}`;
                    }
                    return '';
                  }}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="plaza"
                label="No. Plaza"
              >
                <Select
                  labelProp="clave_y_descripcion"
                  keyProp="id"
                  dataSource={plazas}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="seguridad_social_anterior"
                label="Seguridad Social Anterior"
                hasFeedback
              >
                <Select
                  dataSource={modalidadSeguridadSocial}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="seguridad_social_nueva"
                rules={rules.required}
                label="Seguridad Social Nueva"
                hasFeedback
              >
                <Select
                  dataSource={modalidadSeguridadSocial}
                  disabled={disableFields}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="fecha_inicial"
                rules={rules.required}
                label="Fecha Inicial"
                hasFeedback
              >
                <DatePicker
                  allowClear
                  format="YYYY-MM-DD"
                  disabled={disableFields}
                  placeholder=""
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="fecha_final"
                label="Fecha Final"
                hasFeedback
              >
                <DatePicker
                  allowClear
                  format="YYYY-MM-DD"
                  disabled={isAdding || selectedData?.estados_globales === ESTATUS_INACTIVO}
                  placeholder=""
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                hasFeedback
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  activeInactive={selectedData?.estados_globales === ESTATUS_ACTIVO}
                  disabled={selectedRowKeys.length <= 0 || disableFields}
                  onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Asignación de Seguridad Social ${modalidadSeguridadSocial.find((e) => e.id === selectedData?.seguridad_social_nueva)?.descripcion}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar la Asignación de Seguridad Social ${modalidadSeguridadSocial.find((e) => e.id === selectedData?.seguridad_social_nueva)?.descripcion}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`Inactivar la Asignación de Seguridad Social ${modalidadSeguridadSocial.find((e) => e.id === selectedData?.seguridad_social_nueva)?.descripcion}`}
          loading={loading}
        />

        <CustomSearch
          form={form}
          open={openSearch}
          setVisible={setVisibleSearch}
          modalTitle="Buscar Empleado"
          firstInputLabel="No. de Empleado"
          firstInputName="numero_de_empleado"
          secondInputLabel="Nombre del Empleado"
          secondInputName="nombre"
          searchInputLabel="Apellido Paterno"
          searchInputName="apellido_paterno"
          thirdInputLabel="Apellido Materno"
          thirdInputName="apellido_materno"
          firstSelectLabel="Tipo de Nómina Empleado"
          firstSelectName="tipo_de_nomina_empleado"
          firstSelectDataSource={tiposDeNominaEmpleado}
          inputsNeeded={3}
          selectsNeeded={1}
          paginatedTable
          modalWidth={1400}
          columns={columnsEmpleado}
          baseURI={baseURIPagination}
          setSelectedRow={setSelectedEmpleadoSearch}
          onFinishGet={getEmpleadosConDatosOpFilteredPaginated}
          data={dataSearch}
          setData={setDataSearch}
          searchData={searchData}
          onChange={(row) => {
            setSelectedEmpleadoSearch(row);
          }}
        />
      </Spin>
    </Row>
  );
};

export default SeguridadSocialCambios;
